import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { messageFromError } from "../../../shared/api/drfErrors";
import {
  getLifecycleNotice,
  getOwnerLifecycleActions,
  isModerationLocked,
} from "../../realty/lib/propertyLifecycle";
import {
  togglePropertyBookmark,
  togglePropertyLike,
} from "../api/realty";
import { hasVisibleCooperationOffer } from "../../realty/lib/cooperationOffer";
import PresentationManagerModal from "../../clientPresentation/widgets/PresentationManagerModal";

function DetailActionsPanel({
  property,
  isOwner,
  isAuthenticated = false,
  following = false,
  isFollowing = false,
  isMutatingStatus = false,
  onProposeClient,
  onStartInternalDeal,
  ownerContactPrefill = null,
}) {
  // ownerContactPrefill is historical naming: values come from the logged-in
  // realtor (userInfo), never from the viewed Property owner's profile.
  const id = property.id;
  const ownerActions = isOwner
    ? getOwnerLifecycleActions(property, { isOwner: true }).filter(
        (action) => action.id !== "delete",
      )
    : [];
  const deleteAction = isOwner
    ? getOwnerLifecycleActions(property, { isOwner: true }).find(
        (a) => a.id === "delete",
      )
    : null;
  const lifecycleNotice = isOwner ? getLifecycleNotice(property) : null;
  const moderationLocked = isOwner && isModerationLocked(property);
  const hasOffer = hasVisibleCooperationOffer(property.cooperation_offer);
  const status = property.status;
  const canManagePresentations = Boolean(property && property.can_manage_presentations);
  const showProposeClient =
    !isOwner &&
    status === "active" &&
    hasOffer &&
    typeof onProposeClient === "function";
  const showPartnerPresentations =
    isAuthenticated &&
    !isOwner &&
    canManagePresentations;
  const showPresentationsEntry =
    canManagePresentations && (isOwner || isAuthenticated);
  const showStartInternal =
    isOwner &&
    (status === "active" || status === "reserved") &&
    typeof onStartInternalDeal === "function";

  const [liked, setLiked] = useState(property.liked_by_me ?? false);
  const [bookmarked, setBookmarked] = useState(
    property.bookmarked_by_me ?? false,
  );
  const [isLiking, setIsLiking] = useState(false);
  const [isBookmarking, setIsBookmarking] = useState(false);
  const [engagementError, setEngagementError] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [presentationsOpen, setPresentationsOpen] = useState(false);
  const linkCopiedTimerRef = useRef(null);

  const handleToggleLike = async () => {
    if (isLiking) return;
    setEngagementError(null);
    setIsLiking(true);
    try {
      const data = await togglePropertyLike(id);
      setLiked(Boolean(data && data.liked));
    } catch (err) {
      console.error("DetailActionsPanel: like toggle failed", err);
      setEngagementError(messageFromError(err));
    } finally {
      setIsLiking(false);
    }
  };

  const handleToggleBookmark = async () => {
    if (isBookmarking) return;
    setEngagementError(null);
    setIsBookmarking(true);
    try {
      const data = await togglePropertyBookmark(id);
      setBookmarked(Boolean(data && data.bookmarked));
    } catch (err) {
      console.error("DetailActionsPanel: bookmark toggle failed", err);
      setEngagementError(messageFromError(err));
    } finally {
      setIsBookmarking(false);
    }
  };

  const handleCopyLink = async () => {
    setEngagementError(null);
    const url =
      (typeof window !== "undefined" && window.location && window.location.href) ||
      "";
    try {
      if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
      ) {
        await navigator.clipboard.writeText(url);
      } else {
        throw new Error("Clipboard API недоступний у цьому браузері.");
      }
      setLinkCopied(true);
      if (linkCopiedTimerRef.current) {
        window.clearTimeout(linkCopiedTimerRef.current);
      }
      linkCopiedTimerRef.current = window.setTimeout(
        () => setLinkCopied(false),
        2000,
      );
    } catch (err) {
      console.error("DetailActionsPanel: copy-link failed", err);
      setEngagementError(messageFromError(err));
    }
  };

  useEffect(() => {
    return () => {
      if (linkCopiedTimerRef.current) {
        window.clearTimeout(linkCopiedTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showPresentationsEntry) {
      setPresentationsOpen(false);
    }
  }, [showPresentationsEntry]);

  return (
    <div className="actions-panel" aria-label="Дії з оголошенням">
      {isOwner ? (
        <div className="actions-panel__group">
          <h4 className="actions-panel__title">Робота з оголошенням</h4>
          {lifecycleNotice ? (
            <p className="ap-helper" role="note">
              {moderationLocked ? (
                <i className="fa-solid fa-shield-halved" aria-hidden="true" />
              ) : null}{" "}
              {lifecycleNotice}
            </p>
          ) : null}
          <div className="actions-grid">
            <Link to={`/realty/${id}/edit`} className="action-btn">
              <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
              Редагувати
            </Link>
            {showStartInternal ? (
              <button
                type="button"
                className="action-btn"
                onClick={onStartInternalDeal}
                aria-label="Створити внутрішню угоду"
              >
                <i className="fa-solid fa-file-signature" aria-hidden="true" />
                Створити внутрішню угоду
              </button>
            ) : null}
            {canManagePresentations ? (
              <button
                type="button"
                className="action-btn"
                data-action="client-presentations"
                onClick={() => setPresentationsOpen(true)}
              >
                <i className="fa-solid fa-file-lines" aria-hidden="true" />
                Презентації для клієнтів
              </button>
            ) : null}
            {ownerActions.map((action) => (
              <button
                key={action.id}
                type="button"
                className={[
                  "action-btn",
                  action.successClass,
                  action.dangerClass,
                ]
                  .filter(Boolean)
                  .join(" ")}
                data-bs-toggle="modal"
                data-bs-target={`#${action.modalId}`}
                disabled={isMutatingStatus}
              >
                <i className={action.icon} aria-hidden="true" />
                {action.label}
              </button>
            ))}
            {deleteAction ? (
              <button
                type="button"
                className="action-btn action-btn--danger"
                data-bs-toggle="modal"
                data-bs-target={`#${deleteAction.modalId}`}
                disabled={isMutatingStatus}
              >
                <i className={deleteAction.icon} aria-hidden="true" />
                {deleteAction.label}
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="actions-panel__group">
          <h4 className="actions-panel__title">Реакції</h4>
          <div className="actions-grid actions-grid--reactions">
            {showProposeClient ? (
              <button
                type="button"
                className="action-btn"
                onClick={onProposeClient}
                aria-label="Запропонувати клієнта"
              >
                <i className="fa-solid fa-handshake" aria-hidden="true" />
                Запропонувати клієнта
              </button>
            ) : null}
            {showPartnerPresentations ? (
              <button
                type="button"
                className="action-btn"
                data-action="client-presentations"
                onClick={() => setPresentationsOpen(true)}
              >
                <i className="fa-solid fa-file-lines" aria-hidden="true" />
                Презентації для клієнтів
              </button>
            ) : null}
            <button
              type="button"
              className="action-btn"
              data-action="copy-link"
              onClick={handleCopyLink}
            >
              <i className="fa-solid fa-link" aria-hidden="true" />
              {linkCopied ? "Посилання скопійовано" : "Скопіювати посилання"}
            </button>
            <label className="like action-like" id="like" aria-label="Вподобати">
              <span className="action-like__label">Вподобати</span>
              <span className="heart">
                <input
                  type="checkbox"
                  className="checkbox"
                  id="heart"
                  checked={liked}
                  onChange={handleToggleLike}
                  disabled={isLiking}
                />
                <span className="svg-container">
                  <svg
                    viewBox="0 0 24 24"
                    className="svg-outline"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M17.5,1.917a6.4,6.4,0,0,0-5.5,3.3,6.4,6.4,0,0,0-5.5-3.3A6.8,6.8,0,0,0,0,8.967c0,4.547,4.786,9.513,8.8,12.88a4.974,4.974,0,0,0,6.4,0C19.214,18.48,24,13.514,24,8.967A6.8,6.8,0,0,0,17.5,1.917Zm-3.585,18.4a2.973,2.973,0,0,1-3.83,0C4.947,16.006,2,11.87,2,8.967a4.8,4.8,0,0,1,4.5-5.05A4.8,4.8,0,0,1,11,8.967a1,1,0,0,0,2,0,4.8,4.8,0,0,1,4.5-5.05A4.8,4.8,0,0,1,22,8.967C22,11.87,19.053,16.006,13.915,20.313Z" />
                  </svg>
                  <svg
                    viewBox="0 0 24 24"
                    className="svg-filled"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M17.5,1.917a6.4,6.4,0,0,0-5.5,3.3,6.4,6.4,0,0,0-5.5-3.3A6.8,6.8,0,0,0,0,8.967c0,4.547,4.786,9.513,8.8,12.88a4.974,4.974,0,0,0,6.4,0C19.214,18.48,24,13.514,24,8.967A6.8,6.8,0,0,0,17.5,1.917Z" />
                  </svg>
                </span>
              </span>
            </label>

            <label
              className="ui-bookmark like action-bookmark"
              aria-label="Зберегти в обране"
            >
              <span className="action-bookmark__label">Зберегти</span>
              <input
                type="checkbox"
                checked={bookmarked}
                onChange={handleToggleBookmark}
                disabled={isBookmarking}
              />
              <span className="bookmark">
                <svg viewBox="0 0 32 32">
                  <g>
                    <path d="M27 4v27a1 1 0 0 1-1.625.781L16 24.281l-9.375 7.5A1 1 0 0 1 5 31V4a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4z" />
                  </g>
                </svg>
              </span>
            </label>

            <button
              type="button"
              className="action-btn"
              data-bs-toggle="modal"
              data-bs-target="#content-setting-follow"
              disabled={following || isFollowing}
            >
              <i className="fa-solid fa-user-plus" aria-hidden="true" />
              Стежити
            </button>
            <button
              type="button"
              className="action-btn action-btn--danger"
              data-bs-toggle="modal"
              data-bs-target="#content-setting-complaint"
            >
              <i className="fa-solid fa-flag" aria-hidden="true" />
              Скарга
            </button>
          </div>
        </div>
      )}
      {engagementError ? (
        <p className="actions-panel__error" role="alert">
          {engagementError}
        </p>
      ) : null}
      {showPresentationsEntry && presentationsOpen ? (
        <PresentationManagerModal
          key={property.id}
          open
          property={property}
          ownerContactPrefill={ownerContactPrefill}
          partnerPriceLocked={!isOwner}
          onClose={() => setPresentationsOpen(false)}
        />
      ) : null}
    </div>
  );
}

export default DetailActionsPanel;
